import { FC, useState, useEffect } from "react";
import { FormControl, GridLegacy as Grid, Box } from "@mui/material";

import DateField from "@/components/editable/EditableField/fields/DateField";
import TimeField from "@/components/editable/EditableField/fields/TimeField";
import { Nullable } from "@/types/utility";
import { useDate } from "@/utility/useDate";


interface IDateTimePickerProps {
  minDate?: Date;
  maxDate?: Date;
  onChangeDateTime: (value: Nullable<string>) => void;
  value?: Nullable<string>;
  label?: string;
  timeLabel?: string;
  testIdPrefix?: string;
}

export const DateTimePicker: FC<IDateTimePickerProps> = ({
  onChangeDateTime,
  value,
  label,
  timeLabel,
  minDate,
  maxDate,
  testIdPrefix = "datetime"
}) => {
  const { localizeDate } = useDate();
  const [dateValue, setDateValue] = useState<Nullable<string>>(value || null);
  const [timeValue, setTimeValue] = useState<string>(value || "");

  useEffect(() => {
    if (value) {
      setTimeValue(value);
      setDateValue(value);
    }
  }, [value]);

  const combineDateTime = (date: string, time: string): string => {
    const dateObj = new Date(date);
    const timeObj = new Date(time);

    const combined = new Date(
      dateObj.getFullYear(),
      dateObj.getMonth(),
      dateObj.getDate(),
      timeObj.getHours(),
      timeObj.getMinutes(),
      timeObj.getSeconds()
    );

    return localizeDate(combined).toISO() || "";
  };

  const handleDateChange = (newDateValue: Nullable<string>) => {
    if (!newDateValue) {
      onChangeDateTime(null);
      setDateValue(null);
      return;
    }

    setDateValue(newDateValue);

    if (timeValue) {
      const combined = combineDateTime(newDateValue, timeValue);
      onChangeDateTime(combined);
    } else {
      onChangeDateTime(newDateValue);
    }
  };

  const handleTimeChange = (newTimeValue: string) => {
    if (!newTimeValue) {
      return;
    }

    setTimeValue(newTimeValue);

    if (dateValue) {
      const combined = combineDateTime(dateValue, newTimeValue);
      onChangeDateTime(combined);
    }
  };

  return (
    <Grid item sm={8} md={4} xs={12}>
      <FormControl size="small" fullWidth>
        <Box sx={{ display: "flex", flexDirection: "row", gap: 1, alignItems: "stretch" }}>
          <Box sx={{ minWidth: 160, flex: 1 }}>
            <DateField
              label={label || ""}
              value={dateValue || ""}
              onChange={handleDateChange}
              testId={`criteria-${testIdPrefix}-date-picker`}
              minDate={minDate}
              maxDate={maxDate}
            />
          </Box>
          <Box sx={{ minWidth: 140, flex: 1, display: "flex", alignItems: "flex-start" }}>
            <Box sx={{
              width: "100%",
              // label
              "& > div > div:last-child": {
                color: "rgba(0, 0, 0, 0.6) !important"
              },
              "& .MuiInput-underline:before": {
                borderBottom: "none !important"
              },
              "& .MuiInput-underline:after": {
                borderBottom: "none !important"
              },
              "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                borderBottom: "none !important"
              }
            }}>
              <TimeField
                label={timeLabel || "Time"}
                value={timeValue}
                onChange={handleTimeChange}
                testId={`criteria-${testIdPrefix}-time-picker`}
                fiveMinutes={false}
                memoise={false}
                localise={true}
              />
            </Box>
          </Box>
        </Box>
      </FormControl>
    </Grid>
  );
};

export default DateTimePicker;
